/**
 * Supabase API Service
 * Handles all queries to Supabase for attractions, events, and listings
 */

import { createClient } from '@supabase/supabase-js';
import { getDateOnlyFromValue } from '../eventLocalTime';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const CATALOG_EVENT_PAGE_CACHE_TTL_MS = 2 * 60 * 1000;
const CATALOG_EVENT_PAGE_CACHE_MAX_ENTRIES = 100;
const catalogEventPageCache = new Map();

const getCachedCatalogEventPage = key => {
  const cached = catalogEventPageCache.get(key);
  if (!cached || cached.expiresAt <= Date.now()) {
    if (cached) catalogEventPageCache.delete(key);
    return null;
  }
  catalogEventPageCache.delete(key);
  catalogEventPageCache.set(key, cached);
  return cached.value;
};

const cacheCatalogEventPage = (key, value) => {
  while (catalogEventPageCache.size >= CATALOG_EVENT_PAGE_CACHE_MAX_ENTRIES) {
    catalogEventPageCache.delete(catalogEventPageCache.keys().next().value);
  }
  catalogEventPageCache.set(key, {
    value,
    expiresAt: Date.now() + CATALOG_EVENT_PAGE_CACHE_TTL_MS,
  });
};

export const clearCatalogEventPageCache = () => catalogEventPageCache.clear();

/**
 * Count ShareTribe events for an attraction (grouped by event name + date)
 * @param {Object} sdk - ShareTribe SDK instance (optional)
 * @param {string} attractionName - Name of attraction
 * @returns {Promise<number>} Number of unique ShareTribe events
 */
const countShareTribeEvents = async (sdk, attractionName) => {
  if (!sdk || !attractionName) {
    return 0;
  }

  try {
    // Search ShareTribe listings by attraction name
    const response = await sdk.listings.query({
      keywords: attractionName,
      include: ['author', 'images'],
      'fields.listing': ['title', 'description', 'price', 'publicData', 'metadata'],
      per_page: 50,
    });

    const listings = response.data.data || [];

    // Filter out Ticketmaster/SeatGeek listings (only internal ShareTribe listings)
    const internalListings = listings.filter(listing => {
      const publicData = listing.attributes?.publicData || {};
      const source = publicData.source || '';
      return source !== 'ticketmaster' && source !== 'seatgeek';
    });

    // Group listings by event name + date (same logic as EventListingsPage)
    const grouped = new Map();

    internalListings.forEach(listing => {
      const publicData = listing.attributes?.publicData || {};
      const listingTitle = listing.attributes?.title || '';
      const eventName = (publicData.eventName || listingTitle || '').trim();

      // Extract and normalize date
      let dateString = '';
      let dateValue =
        publicData.event_date ||
        publicData.localDate ||
        publicData.eventDateTime ||
        publicData.eventDate;

      if (dateValue) {
        try {
          dateString = getDateOnlyFromValue(dateValue) || '';
        } catch (e) {
          // Ignore date parsing errors
        }
      }

      // Match to attraction using same logic as EventListingsPage
      const normalizedAttractionName = attractionName.toLowerCase().trim();
      const commonWords = new Set([
        'the',
        'a',
        'an',
        'and',
        'or',
        'for',
        'to',
        'of',
        'in',
        'on',
        'at',
      ]);
      const attractionTerms = normalizedAttractionName
        .split(/[\s,.-]+/)
        .map(term => term.trim())
        .filter(term => term.length > 2 && !commonWords.has(term));

      const listingEventName = eventName.toLowerCase().trim();

      let matchingTermsCount = 0;
      for (const term of attractionTerms) {
        if (listingEventName.includes(term)) {
          matchingTermsCount++;
        }
      }

      const requiredTerms =
        attractionTerms.length <= 2
          ? attractionTerms.length
          : Math.max(2, Math.ceil(attractionTerms.length * 0.6));

      const strictMatch = matchingTermsCount >= requiredTerms;
      const exactContainedMatch = listingEventName.includes(normalizedAttractionName);

      if (strictMatch || exactContainedMatch) {
        const key = `${eventName}|${dateString}`;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key).push(listing);
      }
    });

    return grouped.size;
  } catch (error) {
    console.error('Error counting ShareTribe events:', error);
    return 0;
  }
};

/**
 * Search attractions by name using API endpoint
 * @param {string} query - Search query
 * @param {Object} sdk - ShareTribe SDK instance (kept for existing callers)
 * @param {Object} options - Fetch options
 * @param {AbortSignal} options.signal - Cancels an obsolete search request
 * @returns {Promise<Array>} Array of attractions with event counts
 */
import { apiBaseUrl } from '../api';

export const searchAttractions = async (query, sdk = null, { signal } = {}) => {
  try {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return [];
    }
    // Use API endpoint with query parameter for server-side filtering
    // This is much faster than fetching all attractions and filtering client-side
    const apiUrl = `${apiBaseUrl()}/api/supabase/ticketmaster/attractions/search?q=${encodeURIComponent(
      trimmedQuery
    )}&limit=10`;
    const response = await fetch(apiUrl, {
      credentials: 'include', // Include cookies for authentication
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(`Failed to fetch attractions: ${response.status} ${response.statusText}`);
    }

    const payload = await response.json();
    const attractions = Array.isArray(payload?.attractions) ? payload.attractions : payload;

    if (!Array.isArray(attractions)) {
      console.error('❌ Invalid API response format:', attractions);
      throw new Error('Invalid response format from attractions API');
    }

    // Map to expected format (server already filtered and sorted)
    const attractionsWithCounts = attractions.map(attraction => ({
      id: attraction.id,
      ticketmasterAttractionId: attraction.ticketmasterAttractionId,
      name: attraction.attraction_name || attraction.name,
      image_url: attraction.image_url || attraction.imageUrl,
      category: attraction.category || 'Attraction',
      source: attraction.source || 'ticketmaster_catalog',
      event_count: attraction.activeEventCount || attraction.event_count || 0,
      eventCount: attraction.activeEventCount || attraction.event_count || 0,
    }));
    return attractionsWithCounts;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }
    console.error('❌ Error searching attractions:', error);
    // Re-throw with more context
    throw new Error(`Search failed: ${error.message}`);
  }
};

/**
 * Search venues by name using API endpoint
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of venues
 */
export const searchVenues = async query => {
  try {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const apiUrl = `${apiBaseUrl()}/api/supabase/venues/search?q=${encodeURIComponent(
      query.trim()
    )}&limit=8`;

    const response = await fetch(apiUrl, {
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch venues: ${response.status} ${errorText || response.statusText}`
      );
    }

    const payload = await response.json();
    const venues = Array.isArray(payload?.venues) ? payload.venues : [];

    return venues.map(venue => ({
      id: venue.id,
      name: venue.Name || venue.name || '',
      city: venue.City || venue.city || '',
      state: venue.State || venue.state || '',
      image_url: venue.img_url || venue.image_url || null,
      events: venue.Events || null,
    }));
  } catch (error) {
    console.error('Error searching venues:', error);
    throw error;
  }
};

/**
 * Unified home autocomplete search
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {AbortSignal} [options.signal] - Abort signal for request cancellation
 * @param {number} [options.limit] - Max total suggestions
 * @returns {Promise<Object>} Search payload with grouped sections
 */
export const searchHomeSuggestions = async (query, options = {}) => {
  const trimmedQuery = typeof query === 'string' ? query.trim() : '';
  const { signal, limit = 12 } = options;

  if (trimmedQuery.length < 2) {
    return {
      query: trimmedQuery,
      meta: { took_ms: 0, request_id: null },
      sections: [],
      fallbackAction: null,
    };
  }

  const apiUrl = `${apiBaseUrl()}/api/supabase/search/home?q=${encodeURIComponent(
    trimmedQuery
  )}&limit=${encodeURIComponent(limit)}`;

  const response = await fetch(apiUrl, {
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch home search suggestions: ${response.status} ${errorText ||
        response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get all attractions with event counts
 * @returns {Promise<Array>} Array of all attractions
 */
export const getAllAttractions = async () => {
  try {
    const { data: attractions, error } = await supabase
      .from('Attraction')
      .select('id, attraction_name, category, image_url')
      .order('attraction_name', { ascending: true });

    if (error) throw error;

    // Get event count for each attraction
    const attractionsWithCounts = await Promise.all(
      attractions.map(async attraction => {
        const { count, error: countError } = await supabase
          .from('Events')
          .select('*', { count: 'exact', head: true })
          .eq('attraction_id', attraction.id);

        if (countError) {
          console.error('Error counting events:', countError);
          return { ...attraction, eventCount: 0 };
        }

        return { ...attraction, eventCount: count || 0 };
      })
    );

    return attractionsWithCounts;
  } catch (error) {
    console.error('Error fetching attractions:', error);
    throw error;
  }
};

/**
 * Get events for a specific attraction (paginated)
 * @param {number} attractionId - Attraction ID
 * @param {number} page - Page number (0-indexed)
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} { events, total, hasMore }
 */
export const getEventsByAttraction = async (attractionId, page = 0, limit = 20) => {
  try {
    // IMPORTANT:
    // We fetch SeatData Events via our backend (service role) to avoid RLS / schema-cache relationship issues
    // when querying from the browser.
    const offset = page * limit;
    const url = `${apiBaseUrl()}/api/supabase/ticketmaster/attractions/${encodeURIComponent(
      attractionId
    )}/events?page=${encodeURIComponent(page + 1)}&pageSize=${encodeURIComponent(limit)}`;

    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch events (${response.status}): ${text || response.statusText}`
      );
    }

    const payload = await response.json();
    const events = payload?.events || [];

    return {
      events,
      total: payload?.pagination?.total ?? events.length,
      hasMore: (payload?.pagination?.total ?? events.length) > offset + events.length,
      currentPage: page,
      attractionName: payload?.attraction?.name ?? null,
      attraction: payload?.attraction ?? null,
    };
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

export const getSeatDataEventsByAttraction = async (attractionId, page = 0, limit = 20) => {
  const offset = page * limit;
  const url = `${apiBaseUrl()}/api/supabase/seatdata/events/by-attraction/${encodeURIComponent(
    attractionId
  )}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Failed to fetch SeatData attraction events (${response.status})`);
  }
  const payload = await response.json();
  return {
    events: payload?.events || [],
    total: payload?.count ?? payload?.events?.length ?? 0,
    attractionName: payload?.attractionName || null,
  };
};

/**
 * Get the environment-selected Ticketmaster catalog events for an attraction.
 * The server paginates catalog events first, then enriches only that page's
 * events from csv_events by ticketmaster_event_id.
 */
export const getCatalogEventsByAttraction = async (
  ticketmasterAttractionId,
  page = 0,
  pageSize = 6,
  options = {}
) => {
  try {
    const cacheKey = `${ticketmasterAttractionId}:${page}:${pageSize}`;
    const cached = getCachedCatalogEventPage(cacheKey);
    if (cached) return cached;

    const url = `${apiBaseUrl()}/api/supabase/ticketmaster/attractions/${encodeURIComponent(
      ticketmasterAttractionId
    )}/events?page=${encodeURIComponent(page + 1)}&pageSize=${encodeURIComponent(pageSize)}`;
    const response = await fetch(url, { credentials: 'include', signal: options.signal });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch catalog events (${response.status}): ${text || response.statusText}`
      );
    }
    const payload = await response.json();
    const result = {
      events: payload?.events || [],
      total: payload?.pagination?.total ?? payload?.events?.length ?? 0,
      hasMore:
        (payload?.pagination?.total ?? payload?.events?.length ?? 0) >
        page * pageSize + (payload?.events?.length || 0),
      currentPage: page,
      attraction: payload?.attraction || null,
      attractionName: payload?.attraction?.name || null,
      source: payload?.source || null,
    };
    cacheCatalogEventPage(cacheKey, result);
    return result;
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('Error fetching catalog events:', error);
    }
    throw error;
  }
};

/**
 * Get events for a venue query, grouped by attraction
 * @param {string} venueQuery - venue name input from user
 * @param {number} page - page number (0-indexed)
 * @param {number} limit - items per page
 * @returns {Promise<Object>} { groups, total, hasMore, currentPage, venueQuery }
 */
export const getEventsByVenue = async (venueQuery, page = 0, limit = 100, options = {}) => {
  try {
    const trimmed = typeof venueQuery === 'string' ? venueQuery.trim() : '';
    if (!trimmed || trimmed.length < 2) {
      return {
        groups: [],
        total: 0,
        hasMore: false,
        currentPage: page,
        venueQuery: trimmed,
      };
    }

    const offset = page * limit;
    const url = `${apiBaseUrl()}/api/supabase/seatdata/events/by-venue?q=${encodeURIComponent(
      trimmed
    )}&limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`;

    const response = await fetch(url, { credentials: 'include', signal: options.signal });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch venue events (${response.status}): ${text || response.statusText}`
      );
    }

    const payload = await response.json();
    const groups = payload?.groups || [];
    const total = payload?.count ?? 0;

    return {
      groups,
      total,
      hasMore: total === limit,
      currentPage: page,
      venueQuery: payload?.venueQuery ?? trimmed,
    };
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('Error fetching events by venue:', error);
    }
    throw error;
  }
};

/**
 * Get a single event by ID
 * @param {string} eventId - Event ID (UUID string)
 * @returns {Promise<Object>} Event object
 */
export const getEventById = async eventId => {
  try {
    const normalizedId = String(eventId || '').trim();
    const isCsvEventId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      normalizedId
    );
    const eventPath = isCsvEventId ? 'seatdata/events' : 'ticketmaster/events';
    const url = `${apiBaseUrl()}/api/supabase/${eventPath}/${encodeURIComponent(normalizedId)}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }

      const text = await response.text();
      throw new Error(`Failed to fetch event (${response.status}): ${text || response.statusText}`);
    }

    const payload = await response.json();
    return payload?.event || null;
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('Error fetching event:', error);
    }
    throw error;
  }
};

export const resolveLegacyTicketEvent = async (context = {}) => {
  const params = new URLSearchParams();
  const compactParams = {
    eventName: context.eventName,
    eventDate: context.eventDate,
    eventTime: context.eventTime,
    venue: context.venue || context.venueName,
    location: context.location,
    city: context.city,
  };

  Object.entries(compactParams).forEach(([key, value]) => {
    const text = value == null ? '' : String(value).trim();
    if (text) params.set(key, text);
  });

  if (!params.toString()) {
    return { matched: false, reason: 'missing_context' };
  }

  const response = await fetch(
    `${apiBaseUrl()}/api/supabase/seatdata/events/resolve?${params.toString()}`,
    { credentials: 'include' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to resolve legacy ticket event (${response.status}): ${text || response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get CSV-imported price insights for a Ticketmaster event.
 * @param {string} ticketmasterId - Ticketmaster event ID
 * @returns {Promise<Object|null>} CSV event insights payload, or null if unmatched
 */
export const getCsvEventPricingInsightsByTicketmasterId = async (ticketmasterId, context = {}) => {
  const eventName = context?.eventName || null;
  const eventDate = context?.eventDate || null;

  if (!ticketmasterId && (!eventName || !eventDate)) {
    return null;
  }

  const params = new URLSearchParams();
  if (eventName) params.set('eventName', eventName);
  if (eventDate) params.set('eventDate', eventDate);
  if (context?.venueName) params.set('venueName', context.venueName);

  const lookupId = ticketmasterId || 'context';
  const query = params.toString();
  const url = `${apiBaseUrl()}/api/pricing-insights/csv-event/${encodeURIComponent(lookupId)}${
    query ? `?${query}` : ''
  }`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch CSV event pricing insights (${response.status}): ${text ||
        response.statusText}`
    );
  }

  const payload = await response.json();
  return payload?.matched ? payload : null;
};

/**
 * Get all competitor ticket listings
 * @returns {Promise<Array>} Array of all competitor ticket listings
 */
export const getAllListings = async () => {
  try {
    const { data: listings, error } = await supabase
      .from('competitor_ticket_listings')
      .select('*')
      .eq('availability', 'available')
      .order('price', { ascending: true });

    if (error) throw error;
    return listings || [];
  } catch (error) {
    console.error('Error fetching all competitor ticket listings:', error);
    throw error;
  }
};

/**
 * Get listings for a specific event from Listings table (SeatData)
 * @param {string} eventId - Event UUID
 * @returns {Promise<Array>} Array of listings
 */
export const getListingsByEvent = async eventId => {
  try {
    const url = `${apiBaseUrl()}/api/supabase/seatdata/listings/by-event/${encodeURIComponent(
      eventId
    )}?limit=500&offset=0`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch listings (${response.status}): ${text || response.statusText}`
      );
    }

    const payload = await response.json();
    const listings = payload?.listings || [];

    // Backend returns rows from Listings_per_market (up to 10 per marketplace).
    const formattedListings = (listings || []).map(listing => ({
      id:
        listing.id ||
        `${eventId}-${listing.vendor || 'vendor'}-${listing.listing_id || Math.random()}`,
      event_id: listing.event_id || eventId,
      active: true,
      zone: listing.zone || null,
      section: listing.section || null,
      row: listing.row || null,
      quantity_start: listing.quantity || 1,
      quantity: listing.quantity || 1,
      price: listing.price,
      vendor: listing.vendor || 'unknown',
      listing_url: listing.listing_url || listing.source_url || null,
      currency: listing.currency || 'USD',
      availability: listing.availability || 'available',
      deal_score: listing.deal_score ?? null,
    }));
    return formattedListings || [];
  } catch (error) {
    console.error('Error fetching listings:', error);
    throw error;
  }
};

/**
 * Get attraction by ID using API endpoint
 * @param {string|number} attractionId - Attraction ID (UUID)
 * @returns {Promise<Object>} Attraction object with event_count
 */
export const getAttractionById = async attractionId => {
  try {
    const url = `${apiBaseUrl()}/api/supabase/attractions/${encodeURIComponent(attractionId)}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch attraction (${response.status}): ${text || response.statusText}`
      );
    }

    const attraction = await response.json();

    // Map to expected format
    return {
      id: attraction.id,
      name: attraction.attraction_name || attraction.name,
      image_url: attraction.image_url || attraction.image,
      ticketmaster_attraction_id: attraction.ticketmaster_attraction_id || null,
      ticketmasterAttractionId: attraction.ticketmaster_attraction_id || null,
      event_count: attraction.event_count || 0,
    };
  } catch (error) {
    console.error('Error fetching attraction:', error);
    throw error;
  }
};

const normalizeAttractionName = value =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const scoreAttractionNameMatch = (candidateName, targetName) => {
  const candidate = normalizeAttractionName(candidateName);
  const target = normalizeAttractionName(targetName);

  if (!candidate || !target) {
    return 0;
  }

  if (candidate === target) {
    return 100;
  }

  if (
    (candidate.includes(target) || target.includes(candidate)) &&
    Math.min(candidate.length, target.length) >= 4
  ) {
    return 85;
  }

  const targetTerms = target.split(' ').filter(term => term.length > 2);
  if (targetTerms.length > 0 && targetTerms.every(term => candidate.includes(term))) {
    return 80;
  }

  return 0;
};

/**
 * Resolve a Ticketmaster attraction to the local Supabase attraction row, when one exists.
 * This lets Ticketmaster-powered discovery land back on the Supabase-rich event view.
 */
export const resolveSupabaseAttractionForTicketmaster = async ({
  ticketmasterAttractionId,
  name,
} = {}) => {
  const tmAttractionId =
    typeof ticketmasterAttractionId === 'string'
      ? ticketmasterAttractionId.trim()
      : ticketmasterAttractionId;

  if (tmAttractionId) {
    try {
      const directMatch = await getAttractionById(tmAttractionId);
      if (directMatch?.id) {
        return directMatch;
      }
    } catch (error) {
      // Some Supabase rows use internal IDs, so a Ticketmaster ID miss is expected.
    }
  }

  const attractionName = typeof name === 'string' ? name.trim() : '';
  if (attractionName.length < 2) {
    return null;
  }

  try {
    const candidates = await searchAttractions(attractionName);
    if (!Array.isArray(candidates) || candidates.length === 0) {
      return null;
    }

    const rankedCandidates = candidates
      .map(candidate => ({
        candidate,
        score: scoreAttractionNameMatch(
          candidate.name || candidate.attraction_name,
          attractionName
        ),
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    return rankedCandidates[0]?.candidate || null;
  } catch (error) {
    console.warn('Unable to resolve Ticketmaster attraction against Supabase:', error);
    return null;
  }
};

/**
 * Get sales data for a specific event from Sales table (for price trend chart).
 * @param {string} eventId - Event UUID
 * @returns {Promise<Array>} Array of { price, time_stamp, ... }
 */
export const getSalesDataByEvent = async eventId => {
  try {
    const url = `${apiBaseUrl()}/api/supabase/seatdata/salesdata/by-event/${encodeURIComponent(
      eventId
    )}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch sales data (${response.status}): ${text || response.statusText}`
      );
    }

    const payload = await response.json();
    const salesData = payload?.salesData || [];

    return salesData;
  } catch (error) {
    console.error('Error fetching sales data:', error);
    throw error;
  }
};

/**
 * Get precomputed pricing insights for an event from Event_price_insights.
 * @param {string} eventId - Event UUID
 * @returns {Promise<Object|null>} Insights row (or null if missing)
 */
export const getEventPricingInsightsByEvent = async eventId => {
  try {
    const url = `${apiBaseUrl()}/api/supabase/seatdata/pricing-insights/by-event/${encodeURIComponent(
      eventId
    )}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch event pricing insights (${response.status}): ${text ||
          response.statusText}`
      );
    }

    const payload = await response.json();
    return payload?.insights || null;
  } catch (error) {
    console.error('Error fetching event pricing insights:', error);
    throw error;
  }
};

/**
 * Get latest listing insights for an event from Listing_insights.
 * @param {string} eventId - Event UUID (Event_id column)
 * @returns {Promise<Object|null>} Latest insight row (or null if missing)
 */
export const getListingInsightsByEvent = async eventId => {
  try {
    const url = `${apiBaseUrl()}/api/supabase/seatdata/listing-insights/by-event/${encodeURIComponent(
      eventId
    )}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch listing insights (${response.status}): ${text || response.statusText}`
      );
    }

    const payload = await response.json();
    return payload?.insight || null;
  } catch (error) {
    console.error('Error fetching listing insights:', error);
    throw error;
  }
};
